import React from "react";
import { Link } from "react-router-dom";
import PolicyLayout, { EmailLink } from "./PolicyLayout";

export function DataChoicesPolicy() {
  return (
    <PolicyLayout
      title="privacy choices"
      intro="Use this page to understand and exercise privacy rights that may apply where you live."
    >
      <h2>Your available choices</h2>
      <p>Depending on applicable law and the circumstances, you may ask us to:</p>
      <ul>
        <li>confirm whether we process your personal information and provide access to it;</li>
        <li>correct information that is inaccurate or incomplete;</li>
        <li>delete information, subject to lawful retention exceptions;</li>
        <li>restrict or object to certain processing;</li>
        <li>provide eligible information in a portable format;</li>
        <li>withdraw consent for future processing;</li>
        <li>opt out of a sale, sharing for cross-context behavioral advertising, or targeted advertising where those concepts apply; or</li>
        <li>appeal our response to a request where an appeal right applies.</li>
      </ul>

      <h2>Sale and targeted advertising</h2>
      <p>
        We do not sell personal information for money and do not knowingly use storefront information
        for cross-context behavioral advertising. Because those practices are not currently enabled,
        the site does not display a separate “Do Not Sell or Share” toggle. If our practices change,
        we will update the notice and provide any legally required opt-out method.
      </p>

      <h2>How to submit a request</h2>
      <p>
        Email <EmailLink subject="Privacy rights request" /> with the subject “Privacy rights request.”
        Tell us the right you want to exercise, your country or state, and the email or order number
        connected with your request. Do not send identity documents in your first email.
      </p>

      <h2>Verification</h2>
      <p>
        We may ask for information reasonably necessary to match you with our records. The verification
        level will reflect the sensitivity of the request. If we cannot verify a request, we will explain
        what additional information is needed or why we cannot complete it.
      </p>

      <h2>Authorized agents</h2>
      <p>
        Where permitted, an authorized agent may submit a request for you. We may request evidence of
        authority and may contact you directly to verify identity or confirm permission, unless the law
        provides otherwise.
      </p>

      <h2>Timing, appeals, and complaints</h2>
      <p>
        We will acknowledge and respond within the period required by applicable law. If we deny all or
        part of a request, we will provide the reason and any available appeal instructions. You may also
        complain to the privacy or data-protection authority with jurisdiction where you live.
      </p>

      <h2>No unlawful discrimination</h2>
      <p>
        We will not unlawfully deny service, charge a different price, or provide a different level of
        service because you exercised a privacy right. A difference is permitted only where the law
        allows and it is reasonably related to the value or operation of the service.
      </p>

      <h2>Other controls</h2>
      <p>
        You can remove saved cart data through your browser’s site-data settings. Read our
        <Link to="/cookies"> Cookie Policy</Link> and <Link to="/privacy">Privacy Policy</Link> for details.
      </p>
    </PolicyLayout>
  );
}

export function PaymentPolicy() {
  return (
    <PolicyLayout
      title="payment policy"
      intro="This policy explains payment authorization, currency, verification, refunds, and disputed charges."
    >
      <h2>Accepted payment methods</h2>
      <p>
        The payment methods actually available for your device and destination are shown on the hosted
        payment page. Logos displayed on the site indicate commonly supported card networks, but they do
        not guarantee that every method is available for every transaction.
      </p>

      <h2>Currency and total</h2>
      <p>
        Checkout is currently charged in USD. Before payment, review the product subtotal, standard
        shipping charge, and total. Your card issuer may apply exchange-rate adjustments, foreign
        transaction fees, or other bank charges that we do not receive or control.
      </p>

      <h2>Hosted payment processing</h2>
      <p>
        Payment credentials are entered on a third-party hosted payment page. This website sends the
        information needed to create the payment request, including the total, currency, order reference,
        item title, and email address. We do not intentionally collect or store full card numbers or card
        security codes in the storefront application.
      </p>

      <h2>Authorization and capture</h2>
      <p>
        By confirming payment, you authorize the displayed charge and represent that you are permitted to
        use the payment method. A bank authorization or payment receipt does not necessarily mean the order
        has been accepted. Temporary pending authorizations are controlled by the payment provider or bank.
      </p>

      <h2>Verification and declined payments</h2>
      <p>
        We or the payment provider may use authentication and fraud-prevention checks. A payment can be
        declined or an order held or cancelled when authorization fails, information cannot be verified,
        or there is a reasonable security or legal concern. Contact your bank about a decline; do not email
        us a complete card number or security code.
      </p>

      <h2>Duplicate or incorrect charges</h2>
      <p>
        If you see a duplicate or incorrect completed charge, email <EmailLink subject="Payment charge question" />
        with the order number, date, amount, and only the last four digits of the payment card if needed.
        A pending authorization is not always a completed charge.
      </p>

      <h2>Refunds</h2>
      <p>
        Approved refunds are returned to the original payment method unless the law requires another method.
        We cannot control how quickly a bank posts a refund after it is issued. Read the
        <Link to="/returns"> Returns &amp; Refund Policy</Link> for eligibility and timing.
      </p>

      <h2>Chargebacks</h2>
      <p>
        Contact us first when possible so we can investigate. Nothing in this policy removes a lawful right
        to dispute a charge with your payment provider. We may provide order, delivery, return, and
        communication records in response to a payment dispute.
      </p>

      <h2>Payment security</h2>
      <p>
        Never send payment credentials by email. We will not ask for a complete card number or security code
        by email. Report a suspicious message claiming to represent tunrisefield to <EmailLink subject="Suspicious payment message" />.
      </p>
    </PolicyLayout>
  );
}

export function OrderPolicy() {
  return (
    <PolicyLayout
      title="order & cancellation policy"
      intro="This policy covers order review, acceptance, changes, cancellations, pre-orders, and stock problems."
    >
      <h2>Before placing an order</h2>
      <p>
        Review the product, variant, quantity, address, email, shipping charge, and total. Items in a cart
        are not reserved. You are responsible for accurate details, but we will provide remedies required by
        law where a mistake can reasonably be corrected.
      </p>

      <h2>Receipt and acceptance</h2>
      <p>
        A checkout confirmation means we received the payment request. It does not by itself guarantee order
        acceptance. An order is accepted when we confirm dispatch or expressly confirm acceptance. We may
        contact you if an address, variant, payment, stock, or security issue needs clarification.
      </p>

      <h2>Customer cancellation or change</h2>
      <p>
        Email <EmailLink subject="Urgent order change or cancellation" /> as soon as possible with the order
        number. We will try to act before fulfillment, but cannot guarantee a change or cancellation after an
        order enters packing or dispatch. If dispatch cannot be stopped, use the return process. Mandatory
        withdrawal or cancellation rights remain available.
      </p>

      <h2>When we may cancel</h2>
      <p>We may reject or cancel all or part of an order when reasonably necessary because:</p>
      <ul>
        <li>the item is unavailable or inventory data was wrong;</li>
        <li>payment is not authorized or creates a reasonable fraud concern;</li>
        <li>the price or description contained an obvious material error;</li>
        <li>the address cannot be served or verified;</li>
        <li>the order breaches quantity, resale, promotion, sanctions, or legal restrictions; or</li>
        <li>an event outside reasonable control makes fulfillment impracticable.</li>
      </ul>
      <p>If we cancel after payment, we will refund the cancelled amount to the original payment method.</p>

      <h2>Stock shortages and substitutions</h2>
      <p>
        We will not substitute a materially different product without your express agreement. If part of an
        order is unavailable, we may ask whether you prefer a delay, partial shipment, alternative, or refund.
        Silence will not be treated as agreement to a materially different product.
      </p>

      <h2>Pre-orders and special orders</h2>
      <p>
        A product page will identify a pre-order or special-order item and any available dispatch estimate.
        Estimates may change for manufacturing or supply reasons. If a material delay occurs, we will provide
        the notice and choice to continue or cancel required by law. Customized goods may become non-cancellable
        after production starts where legally permitted and clearly disclosed.
      </p>

      <h2>Quantity limits and resale</h2>
      <p>
        We may apply reasonable quantity limits to protect availability and prevent fraud. Unless expressly
        agreed, products are sold for end use, not unauthorized commercial resale. A limit will not be applied
        in an unlawfully discriminatory manner.
      </p>

      <h2>Delayed fulfillment</h2>
      <p>
        If we cannot ship within a promised period, we will notify you and offer the continuation, cancellation,
        or refund choice required by applicable law. Read our <Link to="/shipping">Shipping &amp; Delivery Policy</Link>.
      </p>
    </PolicyLayout>
  );
}

export function PricingPolicy() {
  return (
    <PolicyLayout
      title="pricing, taxes & duties"
      intro="This policy explains displayed prices, taxes, customs charges, conversion fees, and corrections."
    >
      <h2>Displayed currency</h2>
      <p>
        Product and checkout prices are currently displayed and charged in USD unless clearly stated otherwise.
        A currency symbol refers to US dollars in the current storefront.
      </p>

      <h2>What the checkout total includes</h2>
      <p>
        The checkout total includes the selected products and the delivery charge shown before payment. Any sales,
        value-added, goods-and-services, or similar tax collected by us will be identified at checkout where the
        system supports and applicable law requires collection.
      </p>

      <h2>Import duties and local charges</h2>
      <p>
        Unless checkout expressly identifies an order as duties paid, destination-country duties, taxes, brokerage,
        and import fees are not included and may be collected from the recipient. The amount is set by local
        authorities or carriers and is outside our control. Refusing an import charge may cause the parcel to be
        returned; any refund will be handled under mandatory law and our return policy.
      </p>

      <h2>Bank and currency conversion fees</h2>
      <p>
        Your bank or payment provider chooses its exchange rate and may add foreign transaction, conversion, or
        cross-border fees. These charges are not paid to tunrisefield and are not refundable by us.
      </p>

      <h2>Price changes</h2>
      <p>
        We may change prices prospectively. A later sale or price change does not alter an accepted order unless
        required by law. We do not offer a general price-match or post-purchase price-adjustment promise.
      </p>

      <h2>Obvious errors</h2>
      <p>
        We may correct an obvious pricing, tax, discount, or description error before acceptance. If payment has
        been taken, we will ask whether you want to proceed at the correct price or cancel for a refund, except where
        applicable law requires a different result. We will not use this clause to avoid a valid promotion merely
        because demand was higher than expected.
      </p>

      <h2>Invoices and customs documents</h2>
      <p>
        We may include accurate transaction information on invoices and customs declarations. We cannot declare a
        lower value or mark a commercial purchase as a gift. For an order document, contact <EmailLink subject="Order document request" />.
      </p>
    </PolicyLayout>
  );
}

export function WarrantyPolicy() {
  return (
    <PolicyLayout
      title="product warranty policy"
      intro="This policy explains support for manufacturing defects and preserves all mandatory legal guarantees."
    >
      <div className="policy-callout">
        <p>
          Legal rights for goods that are faulty, unsafe, misdescribed, or not of satisfactory quality are not
          replaced or reduced by this policy.
        </p>
      </div>

      <h2>Coverage</h2>
      <p>
        If a product has a manufacturing or material defect present at delivery, contact us with the order number,
        a description, and photographs or video where useful. Depending on applicable law and the circumstances,
        the remedy may be repair, replacement, price reduction, or refund.
      </p>

      <h2>Examples of issues that may be covered</h2>
      <ul>
        <li>binding, hardware, or components separating during normal initial use;</li>
        <li>a pen mechanism or closure that does not function as described on arrival;</li>
        <li>a substantial manufacturing flaw that prevents ordinary intended use; or</li>
        <li>a product materially different from the description or selected variant.</li>
      </ul>

      <h2>What is not a manufacturing defect</h2>
      <ul>
        <li>ordinary wear, cosmetic aging, patina, or reasonable variation in natural materials;</li>
        <li>damage from accident, misuse, improper storage, alteration, incompatible refills or ink, or failure to follow care instructions;</li>
        <li>expected ink, paper, or color variation that was accurately disclosed; or</li>
        <li>issues caused by an item not supplied by us.</li>
      </ul>
      <p>These exclusions apply only where they are relevant to the cause of the problem and lawful.</p>

      <h2>How to make a claim</h2>
      <p>
        Email <EmailLink subject="Product warranty claim" />. Do not dispose of the product or packaging until we
        respond. We may request reasonable evidence or return of the item. We cover reasonable return costs for a
        confirmed covered defect where required by law.
      </p>

      <h2>Third-party manufacturer warranties</h2>
      <p>
        A product may also include a manufacturer warranty. That warranty is additional to, and does not replace,
        rights you have against us as the seller. Any third-party warranty is governed by its stated terms.
      </p>

      <h2>Time limits</h2>
      <p>
        Report a problem promptly so it can be assessed, but any mandatory limitation or legal-guarantee period in
        your location continues to apply. Our 14-day voluntary return period is not a limit on a valid defect claim.
      </p>
    </PolicyLayout>
  );
}

export function ProductSafetyPolicy() {
  return (
    <PolicyLayout
      title="product safety & recalls"
      intro="Safe use comes first. This page explains care, incident reporting, and our recall process."
    >
      <h2>Use products as intended</h2>
      <p>
        Read product labels and instructions before use. Stationery may contain small parts, sharp nibs or edges,
        glass containers, dyes, adhesives, magnets, or other materials that require care. Keep ink and small parts
        away from young children and pets. Do not ingest ink or apply it to skin unless the product is expressly
        described for that purpose.
      </p>

      <h2>Ink and writing tools</h2>
      <ul>
        <li>Use only compatible ink or refills and follow the tool manufacturer’s cleaning instructions.</li>
        <li>Protect work surfaces and clothing; dyes may stain.</li>
        <li>Stop using a leaking, cracked, unusually hot, or damaged product.</li>
        <li>Review a product’s safety data or manufacturer instructions when supplied.</li>
      </ul>

      <h2>Allergies and sensitivities</h2>
      <p>
        Materials are described when information is available, but individual sensitivities differ. Contact us
        before purchase if you need a material detail. Stop use and seek appropriate medical advice if a reaction
        occurs. This information is general product guidance, not medical advice.
      </p>

      <h2>Report a safety issue</h2>
      <p>
        Stop using the product and email <EmailLink subject="Urgent product safety report" /> with the product name,
        order number, issue, date, and photographs if safe to take. For an immediate danger or injury, contact local
        emergency or poison-control services first.
      </p>

      <h2>Recalls and safety notices</h2>
      <p>
        If we learn of a material safety issue, we will investigate, cooperate with suppliers and authorities, and
        take appropriate action. This may include a stop-use notice, repair, replacement, refund, or recall. Where
        contact details are available and use is lawful, we may send an order-related safety message even if you did
        not opt in to marketing.
      </p>

      <h2>Authenticity and modification</h2>
      <p>
        Use replacement components that meet the product’s stated specifications. Unauthorized modification can
        create risk and may affect remedies only where the modification caused the problem. Suspected counterfeit
        products or unsafe listings can be reported through our <Link to="/intellectual-property">Intellectual Property Policy</Link>.
      </p>
    </PolicyLayout>
  );
}

export function PromotionsPolicy() {
  return (
    <PolicyLayout
      title="promotions policy"
      intro="These rules apply to discount codes, sale prices, bundles, gifts, and pre-order offers."
    >
      <h2>Offer-specific terms</h2>
      <p>
        Each promotion may have its own eligible products, dates, locations, minimum spend, quantity limit, and code.
        Those specific terms apply together with this policy. We will present material restrictions clearly where the
        offer appears.
      </p>

      <h2>Discount codes</h2>
      <p>
        Enter a code before completing payment. Unless the offer states otherwise, one code may be used per order,
        codes cannot be combined, and they have no cash value. A code may be limited to one use per customer, email,
        household, payment method, or order where reasonably necessary to enforce the offer.
      </p>

      <h2>Sale and bundle pricing</h2>
      <p>
        A sale price applies only during the stated period and while eligible stock lasts. A bundle discount is based
        on buying the listed items together. If part of a voluntarily returned bundle is kept, the refund may be
        recalculated using the disclosed standalone or allocated price, where lawful.
      </p>

      <h2>Free gifts</h2>
      <p>
        A gift is available only while stock lasts and may require a qualifying purchase. If the qualifying purchase
        is returned, the unused gift should also be returned; otherwise its disclosed value may be deducted where
        permitted by law. A defective gift remains eligible for legally required remedies.
      </p>

      <h2>Pre-orders</h2>
      <p>
        A pre-order page will disclose that status and any estimated dispatch period. A promotional pre-order price
        does not remove cancellation, delay, or refund rights that apply by law. See our <Link to="/orders">Order &amp; Cancellation Policy</Link>.
      </p>

      <h2>Fair use and errors</h2>
      <p>
        We may refuse duplicated, automated, manipulated, resold, or fraudulently obtained discounts. We may correct
        an obvious technical error before order acceptance and will offer a refund if payment was already taken.
        We will not cancel a valid offer solely because it proved more popular than expected.
      </p>

      <h2>Returns of promotional items</h2>
      <p>
        Promotional and sale items keep all mandatory defect and withdrawal rights. Refunds are based on the amount
        actually paid, subject to any lawful bundle allocation disclosed above. Read our <Link to="/returns">Returns &amp; Refund Policy</Link>.
      </p>
    </PolicyLayout>
  );
}

export function AcceptableUsePolicy() {
  return (
    <PolicyLayout
      title="acceptable use policy"
      intro="These rules help keep the tunrisefield store lawful, secure, and available to genuine customers."
    >
      <h2>Permitted use</h2>
      <p>
        You may browse the store, compare products, place genuine orders, manage your cart, and contact support for
        lawful personal or legitimate business purposes.
      </p>

      <h2>Prohibited conduct</h2>
      <p>You must not:</p>
      <ul>
        <li>break the law, infringe intellectual property, privacy, publicity, or other rights;</li>
        <li>submit false identity, address, payment, order, review, claim, or rights-report information;</li>
        <li>introduce malware, probe vulnerabilities without written authorization, bypass access controls, or disrupt the service;</li>
        <li>use bots or automated requests in a way that places unreasonable load on the site, defeats purchase limits, or extracts protected content;</li>
        <li>conduct payment fraud, promotion abuse, chargeback abuse, unauthorized resale, or money laundering;</li>
        <li>harass, threaten, deceive, impersonate, or unlawfully discriminate against another person; or</li>
        <li>use contact details from the site for spam or unauthorized marketing.</li>
      </ul>

      <h2>Reasonable enforcement</h2>
      <p>
        We may investigate, rate-limit, block, suspend, or cancel activity when we reasonably believe this policy has
        been violated. Measures will be proportionate to the risk, applied lawfully, and will not remove a customer’s
        mandatory rights regarding a completed purchase.
      </p>

      <h2>Security research</h2>
      <p>
        Do not test the live store without written authorization. If you discover a possible vulnerability, avoid
        accessing other people’s information, changing data, or disrupting service, and report it to
        <EmailLink subject="Security vulnerability report" />.
      </p>

      <h2>Reporting abuse</h2>
      <p>
        Report suspected fraud, impersonation, harmful content, or misuse to <EmailLink subject="Acceptable use report" />
        with enough information for us to assess the issue. Do not include unnecessary sensitive data.
      </p>
    </PolicyLayout>
  );
}

export function IntellectualPropertyPolicy() {
  return (
    <PolicyLayout
      title="intellectual property policy"
      intro="tunrisefield respects creators, brands, customers, and other rights holders."
    >
      <h2>Our approach</h2>
      <p>
        We aim to publish original store copy and brand materials, and to use third-party material only with a valid
        permission, license, legal exception, or other lawful basis. We do not intentionally copy another store’s
        protected policy text, photography, product descriptions, logos, or distinctive branding.
      </p>

      <h2>Ownership</h2>
      <p>
        The tunrisefield name and original tunrisefield site materials belong to their applicable rights holder and
        are protected only to the extent the law provides. Third-party names, marks, designs, and materials remain
        the property of their respective owners. No ownership of third-party rights is claimed.
      </p>

      <h2>Non-affiliation</h2>
      <p>
        Reference to a compatible tool, refill, material, payment method, or other third-party product is descriptive
        only. It does not imply sponsorship, endorsement, authorization, or affiliation unless we clearly say so and
        have a lawful basis for that statement.
      </p>

      <h2>Permitted use of this site</h2>
      <p>
        You may view and make limited personal copies of public pages for shopping, records, accessibility, or other
        uses allowed by law. You may not reproduce, sell, falsely attribute, remove rights notices from, or create a
        confusing commercial copy of protected tunrisefield materials without permission. Lawful quotation, review,
        parody, accessibility, interoperability, and other statutory exceptions remain unaffected.
      </p>

      <h2>Your submissions</h2>
      <p>
        You keep ownership of content you send us. As explained in the <Link to="/terms">Terms of Service</Link>,
        we receive only the limited permission needed to address your message, order, claim, or legal obligation.
        Please submit only material you are entitled to share.
      </p>

      <h2>Report a rights concern</h2>
      <p>
        Email <EmailLink subject="Intellectual property rights report" /> with:
      </p>
      <ul>
        <li>your name and a reliable contact method;</li>
        <li>identification of the work, mark, design, or other right;</li>
        <li>the exact page or item at issue;</li>
        <li>why you believe the use is unauthorized; and</li>
        <li>a statement that the report is accurate and made in good faith.</li>
      </ul>
      <p>
        We may request evidence of authority and will review a complete notice promptly. We may remove or limit access
        while investigating. A person affected by a mistake may contact us with supporting information. Knowingly false
        reports may cause harm, so consider obtaining independent advice before filing.
      </p>

      <h2>Counterfeit concerns</h2>
      <p>
        If you believe a product offered or represented as a tunrisefield product is counterfeit, send photographs,
        seller details, and proof of purchase if available. Do not include complete payment credentials.
      </p>
    </PolicyLayout>
  );
}

export function AccessibilityPolicy() {
  return (
    <PolicyLayout
      title="accessibility statement"
      intro="tunrisefield wants customers to browse, understand policies, and complete purchases with equal access."
    >
      <h2>Our accessibility goal</h2>
      <p>
        We work toward a storefront that supports keyboard navigation, readable contrast, meaningful headings and
        links, text alternatives for informative images, clear form labels, zoom, and common assistive technologies.
        Accessibility is an ongoing process as content, browsers, and devices change.
      </p>

      <h2>Known limitations</h2>
      <p>
        Some external services, including a hosted payment page, are operated by third parties and may not provide
        an identical experience. We still want to help you complete a transaction or obtain the same information
        through an accessible alternative where reasonably possible.
      </p>

      <h2>Request help or report a barrier</h2>
      <p>
        Email <EmailLink subject="Accessibility support" /> and include the page, what you were trying to do, the
        barrier, and your preferred way to receive a response. Information about your assistive technology is useful
        but optional. Please do not send medical details.
      </p>

      <h2>Alternative service</h2>
      <p>
        If a website feature is not accessible, we will make reasonable efforts to provide the product information,
        policy, or customer-support service in another usable format. We will not charge an accessibility surcharge.
      </p>

      <h2>Feedback</h2>
      <p>
        We welcome specific feedback and prioritize issues that block checkout, policy access, order support, or
        privacy choices. We aim to acknowledge accessibility messages within 2 business days, although resolution
        time depends on complexity and third-party involvement.
      </p>
    </PolicyLayout>
  );
}

export function ComplaintsPolicy() {
  return (
    <PolicyLayout
      title="complaints & dispute policy"
      intro="We aim to resolve concerns fairly, with a clear written record and without limiting lawful remedies."
    >
      <h2>Step 1: contact us</h2>
      <p>
        Email <EmailLink subject="Formal customer complaint" /> with “Formal customer complaint” in the subject.
        Include your order number, the issue, relevant dates, the outcome you seek, and supporting photographs or
        documents. Do not send complete payment card information.
      </p>

      <h2>Step 2: review</h2>
      <p>
        We aim to acknowledge a complaint within 2 business days and provide a substantive response within 10 business
        days. Complex carrier, payment, safety, or supplier investigations may take longer; if so, we will provide an
        update. These targets do not extend a mandatory legal deadline.
      </p>

      <h2>Step 3: escalation</h2>
      <p>
        If you disagree with the response, reply and request escalation. Explain what was missed and provide any new
        evidence. A different review will be arranged where reasonably practicable.
      </p>

      <h2>External remedies</h2>
      <p>
        You may have the right to contact a consumer-protection, privacy, payment, alternative-dispute-resolution, or
        court body with jurisdiction. This policy does not require private arbitration, prevent a lawful payment
        dispute, shorten a statutory limitation period, or waive collective remedies available by law.
      </p>

      <h2>Fair treatment</h2>
      <p>
        We will assess complaints in good faith and will not retaliate against a customer for raising a genuine concern
        or exercising a legal right. Abusive, threatening, fraudulent, or repetitive communications may be limited to
        a reasonable channel while the underlying issue continues to be addressed as required by law.
      </p>

      <h2>Privacy</h2>
      <p>
        Complaint records are used to investigate, communicate, improve service, prevent fraud, and meet legal duties.
        Read our <Link to="/privacy">Privacy Policy</Link> and <Link to="/data-choices">Privacy Choices</Link> page.
      </p>
    </PolicyLayout>
  );
}

export function SiteNoticePolicy() {
  return (
    <PolicyLayout
      title="website notice & disclaimer"
      intro="This notice explains the status of general site information without reducing rights connected with a purchase."
    >
      <h2>General information</h2>
      <p>
        Editorial and educational content about paper, ink, notebooks, planning, and writing is provided for general
        information. It is not professional, medical, legal, financial, archival-conservation, or safety advice.
        Product-specific descriptions and commitments in an accepted order remain subject to consumer law.
      </p>

      <h2>Accuracy and updates</h2>
      <p>
        We take reasonable care with information but the site may occasionally contain typographical, technical, stock,
        or pricing errors. We may correct prospective website content. Order corrections are handled under our
        <Link to="/orders"> Order &amp; Cancellation Policy</Link> and <Link to="/pricing">Pricing, Taxes &amp; Duties</Link>.
      </p>

      <h2>Product performance</h2>
      <p>
        Paper and ink results depend on nib, ink, pressure, environment, storage, and individual technique. Descriptions
        such as “pen-friendly” communicate intended performance, not identical results with every tool or ink. Any
        express product specification and mandatory guarantee continues to apply.
      </p>

      <h2>Images and color</h2>
      <p>
        We aim to present products accurately. Screen calibration, lighting, production batches, and natural materials
        can create reasonable variation. A material mismatch with the written description is handled under our return
        and warranty policies.
      </p>

      <h2>External links and services</h2>
      <p>
        A link does not automatically imply endorsement or control. Independent sites set their own content, privacy,
        accessibility, and security practices. We remain responsible for obligations that applicable law places on us
        as the seller.
      </p>

      <h2>Availability</h2>
      <p>
        The website may be unavailable during maintenance, security response, provider disruption, or events outside
        reasonable control. We do not guarantee uninterrupted access. If an outage affects an accepted order, contact
        <EmailLink subject="Website or order access problem" />.
      </p>

      <h2>No unlawful exclusion</h2>
      <p>
        Nothing on this page excludes liability or a remedy that cannot legally be excluded, including rights relating
        to faulty or misdescribed goods, fraud, willful misconduct, or other protected matters.
      </p>
    </PolicyLayout>
  );
}
